import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CredentialKind, CredentialSecret, WorkspaceCredential } from '@/lib/api';

const selectClass =
  'flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface CreatePayload {
  kind: CredentialKind;
  name: string;
  provider?: string;
  secret: CredentialSecret;
}

interface Props {
  open: boolean;
  pending: boolean;
  /** When set, the dialog rotates this credential's secret instead of creating one. */
  rotateTarget?: WorkspaceCredential | null;
  onClose: () => void;
  onCreate: (input: CreatePayload) => Promise<void> | void;
  onRotate: (secret: CredentialSecret) => Promise<void> | void;
}

const AI_PROVIDERS = ['anthropic', 'openai'] as const;

export function CredentialDialog({
  open,
  pending,
  rotateTarget,
  onClose,
  onCreate,
  onRotate,
}: Props) {
  const isRotate = Boolean(rotateTarget);
  const [kind, setKind] = useState<CredentialKind>('s3');
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [apiKey, setApiKey] = useState('');

  // Reset on open/close so a stale secret never lingers in component state.
  useEffect(() => {
    if (open) {
      setKind(rotateTarget?.kind ?? 's3');
      setName('');
      setProvider(rotateTarget?.provider || 'anthropic');
      setAccessKeyId('');
      setSecretAccessKey('');
      setApiKey('');
    }
  }, [open, rotateTarget]);

  const effectiveKind = rotateTarget?.kind ?? kind;
  const secretReady =
    effectiveKind === 's3'
      ? accessKeyId.trim().length > 0 && secretAccessKey.trim().length > 0
      : apiKey.trim().length > 0;
  const canSubmit = (isRotate || name.trim().length > 0) && secretReady && !pending;

  function buildSecret(): CredentialSecret {
    return effectiveKind === 's3'
      ? { accessKeyId: accessKeyId.trim(), secretAccessKey: secretAccessKey.trim() }
      : { apiKey: apiKey.trim() };
  }

  function submit() {
    if (!canSubmit) return;
    if (isRotate) {
      void onRotate(buildSecret());
      return;
    }
    void onCreate({
      kind,
      name: name.trim(),
      provider: kind === 's3' ? 'aws' : provider,
      secret: buildSecret(),
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isRotate ? `Rotate “${rotateTarget?.name}”` : 'Add credential'}
      description={
        isRotate
          ? 'Replace the stored secret. The previous secret stops working immediately.'
          : 'Secrets are encrypted at rest and write-once — you can never view the value again, only rotate it.'
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending} data-dialog-close>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {pending ? 'Saving…' : isRotate ? 'Rotate secret' : 'Add credential'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {!isRotate && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="cred-kind">Kind</Label>
              <select
                id="cred-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as CredentialKind)}
                className={selectClass}
              >
                <option value="s3">S3 (AWS access key pair)</option>
                <option value="ai_provider">AI provider (API key)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cred-name">Name</Label>
              <Input
                id="cred-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={kind === 's3' ? 'prod-releases-bucket' : 'anthropic-prod'}
                autoFocus
                required
              />
              <p className="text-[11px] text-muted-foreground">
                Human label, unique within this workspace.
              </p>
            </div>
            {kind === 'ai_provider' && (
              <div className="space-y-1.5">
                <Label htmlFor="cred-provider">Provider</Label>
                <select
                  id="cred-provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className={selectClass}
                >
                  {AI_PROVIDERS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {effectiveKind === 's3' ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="cred-akid">Access key ID</Label>
              <Input
                id="cred-akid"
                value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
                placeholder="AKIA…"
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cred-sak">Secret access key</Label>
              <Input
                id="cred-sak"
                type="password"
                value={secretAccessKey}
                onChange={(e) => setSecretAccessKey(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                required
              />
            </div>
          </>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="cred-apikey">API key</Label>
            <Input
              id="cred-apikey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-…"
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              required
            />
          </div>
        )}

        <p className="rounded-md border border-warning/40 bg-warning/10 p-2.5 text-[11px] text-warning">
          The secret is encrypted on save and never shown again. Store a copy in your own secret
          manager before continuing.
        </p>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
